import { useState, useEffect, useCallback } from 'react';
import { FileText, Filter, Download, BarChart3, Calendar, User, Tag, ShieldCheck, XCircle, CheckCircle, X } from 'lucide-react';
import { loadFromLocalStorage, updateContractRecord } from '@/utils/storage';
import { isAdminSession } from '@/utils/auth';
import type { ContractRecord, ContractStatus } from '@/types';

const STORAGE_KEY = 'contract_records';

export default function ContractRecords() {
  const [records, setRecords] = useState<ContractRecord[]>([]);
  const [filterType, setFilterType] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [dateRange, setDateRange] = useState<'all' | 'week' | 'month' | 'quarter' | 'year'>('all');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewAction, setReviewAction] = useState<'通过' | '驳回'>('通过');
  const isAdmin = isAdminSession();

  const loadRecords = useCallback(() => {
    const data = loadFromLocalStorage<ContractRecord[]>(STORAGE_KEY) || [];
    setRecords(data);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filtered = records.filter(r => {
    if (filterType && !r.templateCategory.includes(filterType)) return false;
    if (filterCompany && !r.ourCompanyName.includes(filterCompany) && !r.partyBName.includes(filterCompany)) return false;
    if (dateRange !== 'all') {
      const d = new Date(r.createdAt);
      const now = new Date();
      switch (dateRange) {
        case 'week':
          if (now.getTime() - d.getTime() > 7 * 24 * 60 * 60 * 1000) return false;
          break;
        case 'month':
          if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'quarter': {
          const q = Math.floor(now.getMonth() / 3);
          const rq = Math.floor(d.getMonth() / 3);
          if (rq !== q || d.getFullYear() !== now.getFullYear()) return false;
          break;
        }
        case 'year':
          if (d.getFullYear() !== now.getFullYear()) return false;
          break;
      }
    }
    return true;
  });

  const handleReview = (id: string, status: '已通过' | '已驳回') => {
    const reviewer = prompt('请输入审核人姓名：') || '管理员';
    updateContractRecord(id, {
      status,
      reviewComment: reviewComment || undefined,
      reviewedBy: reviewer,
      reviewedAt: new Date().toISOString(),
    });
    setReviewingId(null);
    setReviewComment('');
    loadRecords();
  };

  const openReviewModal = (id: string, action: '通过' | '驳回') => {
    setReviewingId(id);
    setReviewAction(action);
    setReviewComment('');
  };

  const exportCsv = () => {
    const headers = ['合同编号', '合同类型', '我方公司', '对方公司', '使用人', '所属公司', '手机号', '生成时间', '核心条款', '审核状态'];
    const rows = filtered.map(r => [
      r.contractNumber,
      r.templateCategory,
      r.ourCompanyName,
      r.partyBName,
      r.employeeName,
      r.employeeCompany,
      r.employeePhone,
      r.createdAt,
      r.contractTerms || '',
      r.status || '待审核',
    ]);
    const csv = [headers, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `合同生成记录_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: filtered.length,
    pending: filtered.filter(r => (r.status || '待审核') === '待审核').length,
    approved: filtered.filter(r => r.status === '已通过').length,
    rejected: filtered.filter(r => r.status === '已驳回').length,
    byType: filtered.reduce((acc, r) => {
      acc[r.templateCategory] = (acc[r.templateCategory] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  const statusBadge = (status?: ContractStatus) => {
    const s = status || '待审核';
    const map: Record<string, { bg: string; text: string }> = {
      '待审核': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      '已通过': { bg: 'bg-green-100', text: 'text-green-800' },
      '已驳回': { bg: 'bg-red-100', text: 'text-red-800' },
    };
    const { bg, text } = map[s] || map['待审核'];
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        {s}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">合同生成记录</h1>
          <p className="text-gray-500 text-sm mt-1">查看和管理所有已生成的合同记录</p>
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
        >
          <Download className="w-4 h-4" />
          导出 CSV
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <FileText className="w-4 h-4" />
            生成总数
          </div>
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <ShieldCheck className="w-4 h-4" />
            待审核
          </div>
          <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            已通过
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <XCircle className="w-4 h-4" />
            已驳回
          </div>
          <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
        </div>
        {Object.entries(stats.byType).slice(0, 1).map(([type, count]) => (
          <div key={type} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Tag className="w-4 h-4" />
              {type}
            </div>
            <div className="text-2xl font-bold text-gray-800">{count}</div>
          </div>
        ))}
      </div>

      {/* 筛选 */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 p-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={dateRange}
          onChange={e => setDateRange(e.target.value as any)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white outline-none"
        >
          <option value="all">全部时间</option>
          <option value="week">近7天</option>
          <option value="month">本月</option>
          <option value="quarter">本季度</option>
          <option value="year">本年度</option>
        </select>
        <input
          type="text"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          placeholder="筛选合同类型"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none w-32"
        />
        <input
          type="text"
          value={filterCompany}
          onChange={e => setFilterCompany(e.target.value)}
          placeholder="筛选公司名"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm outline-none w-40"
        />
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">合同编号</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">类型</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">我方公司</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">对方公司</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">使用人</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">审核状态</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">生成时间</th>
                {isAdmin && <th className="px-4 py-3 text-left font-medium text-gray-600">操作</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-4 py-12 text-center text-gray-400">
                    <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                    <p>暂无记录</p>
                    <p className="text-xs mt-1">生成合同后将自动记录</p>
                  </td>
                </tr>
              )}
              {filtered.map(record => (
                <tr key={record.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-mono text-gray-700">{record.contractNumber}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                      {record.templateCategory}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{record.ourCompanyName}</td>
                  <td className="px-4 py-3 text-gray-700">{record.partyBName}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-gray-700">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      {record.employeeName}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {statusBadge(record.status)}
                    {record.reviewedBy && (
                      <div className="text-xs text-gray-400 mt-1">
                        审核人：{record.reviewedBy}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(record.createdAt).toLocaleString('zh-CN')}
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      {(record.status || '待审核') === '待审核' ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openReviewModal(record.id, '通过')}
                            className="p-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100"
                            title="通过"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openReviewModal(record.id, '驳回')}
                            className="p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                            title="驳回"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {record.reviewedAt && new Date(record.reviewedAt).toLocaleDateString('zh-CN')}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 审核确认弹窗 */}
      {reviewingId && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {reviewAction === '通过' ? '确认通过审核' : '确认驳回'}
              </h3>
              <button onClick={() => setReviewingId(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {reviewAction === '驳回' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">驳回原因（必填）</label>
                  <textarea
                    value={reviewComment}
                    onChange={e => setReviewComment(e.target.value)}
                    placeholder="请填写驳回原因..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  />
                </div>
              )}
              {reviewAction === '通过' && (
                <p className="text-sm text-gray-600">确认通过该合同？通过后状态将变更为"已通过"。</p>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setReviewingId(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
                >
                  取消
                </button>
                <button
                  onClick={() => handleReview(reviewingId, reviewAction === '通过' ? '已通过' : '已驳回')}
                  disabled={reviewAction === '驳回' && !reviewComment.trim()}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
                    reviewAction === '通过'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50`}
                >
                  {reviewAction === '通过' ? '确认通过' : '确认驳回'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
